// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// This file provides the provides functionality to get Secured2 Services.

/* global console fetch */

/**
 *  Calls a Microsoft Graph API and returns the response.
 *
 * @param accessToken The access token to use for the request.
 * @param queryParams Query parameters, e.g., "?$select=name,id". Should start with "?".
 * @returns
 */

import { dumpdebug } from "./utiltyfunctions";
import { appendGlobalconst, getGlobalconst, setGlobalconst, decodeToWindows1252 } from "./utiltyfunctions";
import { getResolveURL } from "../secured2outlook/Scripts/resolveURL";
import { showNotification } from "./utiltyfunctions";

import {
  PROPTRACE,
  PROPBACKUPID,
  PROPFROMEMAIL,
  PROPTOEMAIL,
  PROPCCEMAIL,
  PROPBCCEMAIL,
  PROPSUBJECT,
  stndseperatorstart,
  stndseperatorend,
  PROPERRORTRACE,
  NOTIF_FAIL,
  PROPJWTTOKEN,
  SubscriptionVO,
  OutlookSecurityVO,
  PROPMAILID,
  PROPEMAILUNIQUEID,
} from "./globalconstants";

// paload message formats.
interface ServiceSave {
  contents: string;
  backupid: string;
  docname: string;
  fromEmail: string;
  toEmail: string;
  ccEmail: string;
  bccMail: string;
  emailtype: string;
  app: string;
  subject: string;
}

export async function makeSecured2FetchRequest(
  accessToken: string,
  jwtToken: string,
  serviceuri: string,
  queryParams: { [key: string]: string }
): Promise<any> {
  try {
    // Create a URLSearchParams object
    const params = new URLSearchParams();

    for (const key in queryParams) {
      if (queryParams.hasOwnProperty(key)) {
        // This check ensures the property is not from the prototype chain
        params.append(key, queryParams[key]);
      }
    }

    if (jwtToken && jwtToken.length > 0) {
      params.append("b", jwtToken);
    }

    const url = `${serviceuri}?${params.toString()}`;

    const response = await fetch(url, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json;charset=UTF-8",
        Authorization: accessToken,
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      throw new Error(response.statusText);
    }
  } catch (error) {
    var description = "Secured2 makeSecured2FetchRequest Experienced Error.  Return:" + error;
    appendGlobalconst(PROPERRORTRACE, description, stndseperatorstart, stndseperatorend);
    appendGlobalconst(PROPTRACE, description, stndseperatorstart, stndseperatorend);
    throw new Error(description);
  }
}

export async function makeSecured2PostRequest(
  accessToken: string,
  jwtToken: string,
  serviceuri: string,
  queryParams: { [key: string]: string },
  data: any
): Promise<any> {
  try {
    // Create a URLSearchParams object
    const params = new URLSearchParams();

    for (const key in queryParams) {
      params.append(key, queryParams[key]);
    }

    if (jwtToken && jwtToken.length > 0) {
      params.append("b", jwtToken);
    }

    const url = `${serviceuri}?${params.toString()}`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "multipart/form-data",
        Authorization: accessToken,
      },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      throw new Error(response.statusText);
    }
  } catch (error) {
    var description = "Secured2 makeSecured2PostRequest Experienced Error.  Return:" + error;
    appendGlobalconst(PROPERRORTRACE, description, stndseperatorstart, stndseperatorend);
    appendGlobalconst(PROPTRACE, description, stndseperatorstart, stndseperatorend);
    throw new Error(description);
  }
}

export async function makeSecured2FormPostRequest(
  accessToken: string,
  jwtToken: string,
  serviceuri: string,
  queryParams: { [key: string]: string },
  formDatas: { [key: string]: string }
): Promise<any> {
  return new Promise(async (resolve, reject) => {
    try {
      // Create a URLSearchParams object
      const params = new URLSearchParams();

      for (const key in queryParams) {
        // This check ensures the property is not from the prototype chain
        params.append(key, queryParams[key]);
      }

      if (jwtToken && jwtToken.length > 0) {
        params.append("b", jwtToken);
      }

      const url = `${serviceuri}?${params.toString()}`;

      let formData = new FormData();
      for (const key in formDatas) {
        formData.append(key, formDatas[key]);
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          //"Content-Type": "multipart/form-data",
          Authorization: accessToken,
        },
        body: formData,
      });

      if (response.ok) {
        const data = response.json();
        resolve(data);
      } else {
        reject(response.statusText);
      }
    } catch (error) {
      var description = "Secured2 makeSecured2PostRequest Experienced Error.  Return:" + error;
      appendGlobalconst(PROPERRORTRACE, description, stndseperatorstart, stndseperatorend);
      appendGlobalconst(PROPTRACE, description, stndseperatorstart, stndseperatorend);
      reject(description);
    }
  });
}

export async function makeSecured2FormDataPostRequest(
  accessToken: string,
  jwtToken: string,
  serviceuri: string,
  queryParams: { [key: string]: string },
  formDatas: FormData
): Promise<any> {
  return new Promise(async (resolve, reject) => {
    try {
      // Create a URLSearchParams object
      const params = new URLSearchParams();

      for (const key in queryParams) {
        // This check ensures the property is not from the prototype chain
        params.append(key, queryParams[key]);
      }

      if (jwtToken && jwtToken.length > 0) {
        params.append("b", jwtToken);
      }

      const url = `${serviceuri}?${params.toString()}`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          //"Content-Type": "multipart/form-data",
          Authorization: accessToken,
        },
        body: formDatas,
      });

      if (response.ok) {
        const data = response.json();
        resolve(data);
      } else {
        reject(response.statusText);
      }
    } catch (error) {
      var description = "Secured2 makeSecured2PostRequest Experienced Error.  Return:" + error;
      appendGlobalconst(PROPERRORTRACE, description, stndseperatorstart, stndseperatorend);
      appendGlobalconst(PROPTRACE, description, stndseperatorstart, stndseperatorend);
      reject(description);
    }
  });
}

// secured2 specific calls.
export async function servicecallsave(
  accessToken: string,
  documentname: string,
  textToInsert: Uint8Array
): Promise<any> {
  dumpdebug("TRACE", "start callsave backupid:" + getGlobalconst(PROPBACKUPID));
  appendGlobalconst(
    PROPTRACE,
    "STEP servicecallsave: backupid:" + getGlobalconst(PROPBACKUPID),
    stndseperatorstart,
    stndseperatorend
  );

  try {
    const textToInsertString = decodeToWindows1252(textToInsert);

    const formdata: { [key: string]: string } = {
      contents: textToInsertString,
      backupid: getGlobalconst(PROPBACKUPID),
      docname: documentname,
      fromEmail: getGlobalconst(PROPFROMEMAIL),
      toEmail: getGlobalconst(PROPTOEMAIL),
      ccEmail: getGlobalconst(PROPCCEMAIL),
      bccMail: getGlobalconst(PROPBCCEMAIL),
      emailtype: "HTML",
      app: "email",
      subject: getGlobalconst(PROPSUBJECT),
    };

    const queryParams: { [key: string]: string } = {};
    var uri = await getResolveURL("URLSAVE");
    const result: any = makeSecured2FormPostRequest(
      accessToken,
      getGlobalconst(PROPJWTTOKEN),
      uri,
      queryParams,
      formdata
    );

    return result;
  } catch (error) {
    var description = "Secured2 getTemplates Experienced Error.  Return:" + error;
    appendGlobalconst(PROPERRORTRACE, description, stndseperatorstart, stndseperatorend);
    appendGlobalconst(PROPTRACE, description, stndseperatorstart, stndseperatorend);
    showNotification(NOTIF_FAIL, "Error - " + description);
    throw new Error(description);
  }
}

export async function servicecallsecurity(accessToken: string): Promise<SubscriptionVO, undefined> {
  return new Promise(async (resolve, reject) => {
    dumpdebug("TRACE", "Start servicecallsecurity");
    appendGlobalconst(PROPTRACE, "About to call outlooksecurity", stndseperatorstart, stndseperatorend);

    var vgenbid = true;
    if (typeof getGlobalconst(PROPBACKUPID) !== "undefined" && getGlobalconst(PROPBACKUPID)) {
      vgenbid = false;
    }

    try {
      const uri: string = getResolveURL("URLSECURITY");

      const queryParams: { [key: string]: string } = {
        msid: encodeURIComponent(getGlobalconst(PROPMAILID)),
        emailid: encodeURIComponent(getGlobalconst(PROPFROMEMAIL)),
        genbid: String(vgenbid),
        app: "email",
        checktrial: "true",
      };

      // make call to sercured2 services
      const respdata: OutlookSecurityVO = await makeSecured2FetchRequest(accessToken, "", uri, queryParams);

      // debug info.
      appendGlobalconst(
        PROPTRACE,
        "Security CallBack Success = " + respdata.result + " Backup ID - " + respdata.backupid,
        stndseperatorstart,
        stndseperatorend
      );
      dumpdebug("TRACE", "success servicecallsecurity");
      if (respdata.result == 0) {
        dumpdebug("TRACE", "IF-1 servicecallsecurity");
        if (typeof respdata == "undefined") {
          throw new Error("Was unable to get security token.  Something went wrong");
        } else {
          if (respdata.backupid) {
            // backup token
            setGlobalconst(PROPBACKUPID, respdata.backupid);
          }

          if (respdata.resultIdentifier) {
            //msid token
            setGlobalconst(PROPEMAILUNIQUEID, respdata.resultIdentifier);
          }
        }

        if (typeof respdata.subscriptionVO !== "undefined") {
          if (respdata.subscriptionVO.clientToken) {
            setGlobalconst(PROPJWTTOKEN, respdata.subscriptionVO.clientToken);
            resolve(respdata.subscriptionVO);
          }
        }
      }
    } catch (error) {
      const description = "Secured2 Security Experienced Error. Error:" + error;
      appendGlobalconst(PROPERRORTRACE, description, stndseperatorstart, stndseperatorend);
      appendGlobalconst(PROPTRACE, description, stndseperatorstart, stndseperatorend);
      reject(description);
    }
  });
}

export async function servicereadsecurity(accessToken: string): Promise<SubscriptionVO, undefined> {
  return new Promise(async (resolve, reject) => {
    dumpdebug("TRACE", "Start servicereadsecurity");
    appendGlobalconst(PROPTRACE, "About to call outlookreadsecurity", stndseperatorstart, stndseperatorend);

    try {
      const uri: string = getResolveURL("URLREAD");

      const queryParams: { [key: string]: string } = {
        itemId: encodeURIComponent(getGlobalconst(PROPMAILID)),
        principalUser: encodeURIComponent(getGlobalconst(PROPFROMEMAIL)),
      };

      // make call to sercured2 services
      const respdata: OutlookSecurityVO = await makeSecured2FetchRequest(
        accessToken,
        getGlobalconst(PROPJWTTOKEN),
        uri,
        queryParams
      );

      // debug info.
      appendGlobalconst(
        PROPTRACE,
        "Security CallBack Success = " + respdata.result + " Backup ID - " + respdata.backupid,
        stndseperatorstart,
        stndseperatorend
      );
      dumpdebug("TRACE", "success servicereadsecurity");
      if (respdata.result == 0) {
        dumpdebug("TRACE", "IF-1 servicereadsecurity");
        if (typeof respdata == "undefined") {
          throw new Error("Was unable to get security token.  Something went wrong");
        } else {
          if (respdata.backupid) {
            // backup token
            setGlobalconst(PROPBACKUPID, respdata.backupid);
          }

          if (respdata.resultIdentifier) {
            //msid token
            setGlobalconst(PROPEMAILUNIQUEID, respdata.resultIdentifier);
          }
        }

        if (typeof respdata.subscriptionVO !== "undefined") {
          if (respdata.subscriptionVO.clientToken) {
            setGlobalconst(PROPJWTTOKEN, respdata.subscriptionVO.clientToken);
            resolve(respdata.subscriptionVO);
          }
        }
      }
    } catch (error) {
      const description = "Secured2 Read Security Experienced Error. Error:" + error;
      appendGlobalconst(PROPERRORTRACE, description, stndseperatorstart, stndseperatorend);
      appendGlobalconst(PROPTRACE, description, stndseperatorstart, stndseperatorend);
      reject(description);
    }
  });
}
